/**
 * AffiliateRedirect.tsx
 *
 * Обрабатывает pretty affiliate URLs вида: quantumgame.love/go/ALEX
 *
 * Функции:
 * - Валидация affiliate кода через API
 * - Сохранение в localStorage для последующего track-referral
 * - Редирект на landing page
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';

interface AffiliateResolveResponse {
  valid: boolean;
  affiliate_id: number;
  affiliate_code: string;
}

const AffiliateRedirect: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'validating' | 'valid' | 'invalid'>('validating');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const validateAndRedirect = async () => {
      if (!code) {
        // Полная перезагрузка страницы для загрузки статичного лендинга
        window.location.href = '/';
        return;
      }

      try {
        // Валидируем affiliate код через API
        const response = await fetch(`${config.apiUrl}/api/affiliates/resolve/${code.toUpperCase()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          // Полная перезагрузка страницы
          window.location.href = '/';
          return;
        }

        const data: AffiliateResolveResponse = await response.json();

        if (data.valid) {
          // Сохраняем affiliate код в localStorage
          localStorage.setItem('affiliate_code', data.affiliate_code);

          // Также сохраняем источник и landing page для analytics
          localStorage.setItem('affiliate_source', 'pretty_url');
          localStorage.setItem('affiliate_landing', window.location.href);

          console.log(`✅ Affiliate code saved: ${data.affiliate_code}`);

          // Полная перезагрузка страницы для загрузки статичного лендинга
          window.location.href = '/';
        } else {
          // Полная перезагрузка страницы
          window.location.href = '/';
        }

      } catch (error: any) {
        console.error('Error validating affiliate code:', error);
        // Полная перезагрузка страницы
        window.location.href = '/';
      }
    };

    validateAndRedirect();
  }, [code, navigate]);

  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
      <div className="text-center">
        {status === 'validating' && (
          <>
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Проверяем...</span>
            </div>
            <h4 className="mb-2">Проверяем партнёрскую ссылку...</h4>
            <p className="text-muted">Код: <strong>{code?.toUpperCase()}</strong></p>
          </>
        )}

        {status === 'valid' && (
          <>
            <div className="mb-4" style={{ fontSize: '4rem' }}>
              ✅
            </div>
            <h4 className="mb-2 text-success">Партнёрская ссылка активирована!</h4>
            <p className="text-muted">Перенаправляем на главную страницу...</p>
          </>
        )}

        {status === 'invalid' && (
          <>
            <div className="mb-4" style={{ fontSize: '4rem' }}>
              ❌
            </div>
            <h4 className="mb-2 text-danger">Ошибка валидации</h4>
            <p className="text-muted">{errorMessage}</p>
            <p className="text-muted">Перенаправляем на главную страницу...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AffiliateRedirect;
